import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useApi } from "../hooks/useApi";
import Navigation from "../components/Navigation";
import AssessmentStatusBadge from "../utils/assessmentStatusLabel";
import Card from "../components/Card";
import Button from "../components/Buttons";
import LoadingSpinner from "../components/LoadingSpinner";
import { FileText, Shield, Plus, Trash2, RefreshCw, Users, Eye } from "lucide-react";
import Pagination from "../components/Pagination";
import { usePageSize } from "../hooks/usePageSize";
import DealAssignAssessModal from "../components/DealAssignAssessModal";
import AssessmentRespondentsModal from "../components/AssessmentRespondentsModal";
import AssessmentUnassignedModal from "../components/AssessmentUnassignedModal";
import DarkWebNewBusinessUnitModal from "../components/DarkWebNewBusinessUnitModal";
import DeleteAssessmentModal from './DeleteAssessmentModal';
import ReassessModal from './ReassessModal';
import { useVerticals } from "../contexts/VerticalsContext";
import { useRBAC } from "../hooks/useRBAC";
import { useURLState } from "../hooks/useURLState";
import AssessmentActivityLog from "../components/AssessmentActivityLog";
import { severityPostureScore } from '../utils/riskScoringEngine';

import { SHOW_DELETE_ASSESSMENT } from '../config';
const DarkWebBusinessUnits = () => {
  const [businessUnits, setBusinessUnits] = useState([]);
  const [selectedBU, setSelectedBU] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPageParam, setCurrentPageParam] = useURLState('page');
  const currentPage = parseInt(currentPageParam) || 1;
  const [totalCount, setTotalCount] = useState(0);
  const [paging, setPaging] = useState(false);
  const [pageSize, setPageSize] = usePageSize('darkWebBusinessUnits');
  const listTopRef = useRef(null);

  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedEntityForAssign, setSelectedEntityForAssign] = useState(null);
  const [respondentsAssessmentId, setRespondentsAssessmentId] = useState(null);
  const [showAddBUModal, setShowAddBUModal] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [assessmentToDelete, setAssessmentToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [showReassessModal, setShowReassessModal] = useState(false);
  const [assessmentToReassess, setAssessmentToReassess] = useState(null);
  const [reassessing, setReassessing] = useState(false);

  const [showUnassignedAlert, setShowUnassignedAlert] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const apiCall = useApi();
  const { verticals } = useVerticals();
  const { canAssess, getAssessDisabledReason, canManageOrganizationRisks, canManageBusinessUnit, canViewBusinessUnit, canAssessViaAssignment, canCreateDealOrBusinessUnit, authVersion, canDeleteAssessments } = useRBAC();
  const canDeleteAssessment = SHOW_DELETE_ASSESSMENT && canDeleteAssessments(); // super admin only, and the switch in config.js
  const selectedOrg = JSON.parse(localStorage.getItem("selectedOrganization") || "{}");
  const canManageRisks = canManageOrganizationRisks();
  const canManageRisksForBU = (buId) => canManageRisks || canManageBusinessUnit(buId);
  const canAddDealOrBU = canCreateDealOrBusinessUnit(selectedOrg?.organizationId);

  // Fetch business units with risk summary from optimized endpoint
  const fetchBusinessUnits = async () => {
    setPaging(true);
    try {
      const org = JSON.parse(localStorage.getItem("selectedOrganization"));
      if (!org?.organizationId) {
        setLoading(false);
        return;
      }

      const data = await apiCall("GET", `/businessUnits/summary?category=DarkWeb&page=${currentPage}&pageSize=${pageSize}`);

      const busWithRiskData = (data.items || []).map((bu) => ({
        businessUnitId: bu.businessUnitId,
        businessUnitName: bu.businessUnitName,
        verticalId: bu.verticalId,
        domain: bu.domain,
        hasAssessment: bu.applicationCount > 0,
        riskCounts: {
          CRITICAL: bu.riskCounts?.critical || 0,
          HIGH: bu.riskCounts?.high || 0,
          MEDIUM: bu.riskCounts?.medium || 0,
          LOW: bu.riskCounts?.low || 0,
        },
        totalRisks: (bu.riskCounts?.critical || 0) + (bu.riskCounts?.high || 0) +
                    (bu.riskCounts?.medium || 0) + (bu.riskCounts?.low || 0),
        activeRisks: bu.activeRisks,
        riskScore: calculateRiskScore({
          CRITICAL: bu.riskCounts?.critical || 0,
          HIGH: bu.riskCounts?.high || 0,
          MEDIUM: bu.riskCounts?.medium || 0,
          LOW: bu.riskCounts?.low || 0,
        }),
        riskLevel: bu.riskLevel === "NONE" ? "No Risk" : bu.riskLevel,
        latestAssessment: bu.assessmentId ? {
          darkWebAssessmentID: bu.assessmentId,
          darkWebAssignedToUserId: bu.assignedToUserId,
          darkWebAssignedToUserName: bu.assignedToUserName,
          darkWebStatus: bu.status,
          assigneeUserIds: bu.assigneeUserIds,
        } : null,
      }));

      // Backend /businessUnits/summary already applies RBAC BU-scoping and returns a scoped
      // totalCount, so trust the server response directly. Re-filtering client-side broke
      // pagination for BU-scoped users by clobbering totalCount with the current page's length.
      setBusinessUnits(busWithRiskData);
      setTotalCount(data.totalCount || 0);
    } catch (err) {} finally {
      setLoading(false);
      setPaging(false);
    }
  };

  const calculateRiskScore = (riskCounts) => {
    return severityPostureScore(riskCounts);
  };

  const handleDeleteAssessment = async () => {
    if (!assessmentToDelete) return;
    setDeleting(true);
    try {
      await apiCall('DELETE', `/darkWebAssessments/${assessmentToDelete}`);
      setShowDeleteModal(false);
      setAssessmentToDelete(null);
      await fetchBusinessUnits();
    } catch (error) {} finally {
      setDeleting(false);
    }
  };

  const handleReassessAssessment = async () => {
    if (!assessmentToReassess) return;
    setReassessing(true);
    try {
      await apiCall('POST', `/darkWebAssessments/${assessmentToReassess}/reassess`);
      setShowReassessModal(false);
      setAssessmentToReassess(null);
      navigate(`/DarkWebAssessment/${assessmentToReassess}?step=review`);
    } catch (error) {} finally {
      setReassessing(false);
    }
  };

  const handleBusinessUnitAdded = () => {
    setShowAddBUModal(false);
    setCurrentPageParam(1);
    fetchBusinessUnits();
  };

  useEffect(() => {
    fetchBusinessUnits();
  }, [currentPage, pageSize, authVersion]);

  // Auto-select from navigation state
  useEffect(() => {
    const autoSelectId = location.state?.selectedBusinessUnitId;
    if (autoSelectId && businessUnits.length > 0) {
      const target = businessUnits.find((bu) => bu.businessUnitId === autoSelectId);
      if (target) {
        setSelectedBU(target);
      }
    }
  }, [businessUnits, location.state]);

  const totalPages = Math.ceil(totalCount / pageSize);

  if (loading) return <LoadingSpinner />;

  return (
    <div className="min-h-screen bg-theme-primary">
      <Navigation />

      <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-content-primary">
            <span className="text-blue-800">Dark Web Business Units</span>
          </h1>
          <p className="mt-2 text-gray-600">Manage Dark Web assessments for business units</p>
        </div>

        <Card>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold">Business Units</h2>
            {canAddDealOrBU && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => setShowAddBUModal(true)}
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Business Unit
              </Button>
            )}
          </div>

          <div className="space-y-3" ref={listTopRef}>
            {businessUnits.length === 0 && (
              <p className="text-gray-500 text-center py-4">No business units added yet. Click 'Add Business Unit' to get started.</p>
            )}
            {businessUnits.map((bu) => (
              <div
                key={bu.businessUnitId}
                className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                  selectedBU?.businessUnitId === bu.businessUnitId
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-200 hover:border-gray-300"
                }`}
                onClick={() => setSelectedBU(bu)}
              >
                <div className="flex gap-4 items-start">
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="font-medium text-content-primary">{bu.businessUnitName}<AssessmentStatusBadge status={bu.latestAssessment?.darkWebStatus} /></h3>
                        {bu.businessUnitOwner && <p className="text-sm text-gray-500 mt-1">Owner: {bu.businessUnitOwner}</p>}
                        {bu.latestAssessment?.darkWebAssignedToUserName && (
                          <p className="text-xs text-blue-600 mt-1">
                            Assigned to: <span className="font-semibold">{bu.latestAssessment.darkWebAssignedToUserName}</span>
                          </p>
                        )}
                      </div>
                      <div className="ml-4 text-right">
                        <div className="text-xs text-gray-500 mt-1">
                          {bu.businessUnitCity && bu.businessUnitCountry
                            ? `${bu.businessUnitCity}, ${bu.businessUnitCountry}`
                            : bu.businessUnitCity || bu.businessUnitCountry || ""}
                        </div>
                      </div>
                    </div>
                    {/* Risk details */}
                    {bu.hasAssessment ? (
                      <div className="mt-2">
                        <div className="flex flex-wrap gap-2">
                          {bu.riskCounts?.CRITICAL > 0 && <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded">{bu.riskCounts.CRITICAL} Critical</span>}
                          {bu.riskCounts?.HIGH > 0 && <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded">{bu.riskCounts.HIGH} High</span>}
                          {bu.riskCounts?.MEDIUM > 0 && <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded">{bu.riskCounts.MEDIUM} Medium</span>}
                          {bu.riskCounts?.LOW > 0 && <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">{bu.riskCounts.LOW} Low</span>}
                        </div>
                        <div className="mt-2">
                          <div className="text-xs text-gray-500 mb-1">Risk Score: {bu.riskScore || 0}%</div>
                          <div className="w-full bg-gray-200 rounded-full h-3">
                            <div className={`h-3 rounded-full ${bu.riskScore <= 40 ? "bg-green-500" : bu.riskScore <= 60 ? "bg-yellow-500" : bu.riskScore <= 80 ? "bg-orange-500" : "bg-red-500"}`} style={{ width: `${bu.riskScore || 0}%` }} />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-2 text-xs text-gray-500">No Assessment Data</div>
                    )}
                    <div className="flex flex-wrap gap-2 mt-3">
                  {canManageRisksForBU(bu.businessUnitId) && (
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={bu.hasAssessment && bu.latestAssessment?.darkWebStatus === "completed"}
                      title={bu.hasAssessment && bu.latestAssessment?.darkWebStatus === "completed" ? "Assessment is completed" : ""}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedEntityForAssign(bu);
                        setShowAssignModal(true);
                      }}
                    >
                      <Shield className="h-3 w-3 mr-1" />
                      {bu.latestAssessment?.darkWebAssignedToUserId ? "Reassign" : "Assign"}
                    </Button>
                  )}

                  {canManageRisksForBU(bu.businessUnitId) && bu.latestAssessment?.darkWebAssessmentID && (
                    <Button
                      size="sm"
                      variant="secondary"
                      className="whitespace-nowrap"
                      title="Manage additional respondents for this assessment"
                      onClick={(e) => {
                        e.stopPropagation();
                        setRespondentsAssessmentId(bu.latestAssessment.darkWebAssessmentID);
                      }}
                    >
                      <Users className="h-4 w-4 mr-1" />
                      Respondents
                    </Button>
                  )}

                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={
                      !canAssess(bu.latestAssessment?.darkWebAssignedToUserId, null, 'BUSINESS_UNIT', bu.businessUnitId, bu.latestAssessment?.assigneeUserIds || []) ||
                      (bu.hasAssessment && (bu.latestAssessment?.darkWebStatus === "completed" || bu.latestAssessment?.darkWebStatus === "analyzing"))
                    }
                    title={
                      (bu.latestAssessment?.darkWebStatus === "completed" ? "Assessment is completed"
                        : bu.latestAssessment?.darkWebStatus === "analyzing" ? "Assessment is being analyzed"
                        : "")
                        || getAssessDisabledReason(bu.latestAssessment?.darkWebAssignedToUserId, null, 'BUSINESS_UNIT', bu.businessUnitId, bu.latestAssessment?.assigneeUserIds || [])
                        || ""
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!bu.latestAssessment?.darkWebAssignedToUserId) {
                        setShowUnassignedAlert(true);
                        return;
                      }
                      const dwPath = bu.latestAssessment?.darkWebAssessmentID
                        ? `/DarkWebAssessment/${bu.latestAssessment.darkWebAssessmentID}`
                        : "/DarkWebAssessment";
                      navigate(dwPath, {
                        state: {
                          businessUnitId: bu.businessUnitId,
                          businessUnitName: bu.businessUnitName,
                          organizationId: bu.organizationId,
                          verticalId: bu.verticalId,
                          regionId: bu.regionId,
                          subVerticalId: bu.subVerticalId,
                        },
                      });
                    }}
                  >
                    <FileText className="h-4 w-4 mr-1" />
                    Assess
                  </Button>

                  {/* Viewing mirrors the backend's GetViewableAssessmentAsync: BU scope or assignment. */}
                  {bu.hasAssessment && bu.latestAssessment?.darkWebAssessmentID && bu.latestAssessment?.darkWebStatus === "completed" &&
                    (canViewBusinessUnit(bu.businessUnitId) || canAssessViaAssignment(bu.latestAssessment?.darkWebAssignedToUserId, bu.latestAssessment?.assigneeUserIds || [])) && (
                    <Button
                      size="sm"
                      variant="secondary"
                      title="View the completed assessment's questions, answers, notes, and attachments (read-only)"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/DarkWebAssessment/${bu.latestAssessment.darkWebAssessmentID}?step=review`);
                      }}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View Answers
                    </Button>
                  )}

                  {bu.hasAssessment && bu.latestAssessment?.darkWebAssessmentID && bu.latestAssessment?.darkWebStatus === "completed" ? (
                    <Button
                      size="sm"
                      variant="primary"
                      className="px-1 py-1 h-9 text-xs"
                      disabled={bu.totalRisks === 0}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/DarkWebAssessment/${bu.latestAssessment.darkWebAssessmentID}/risks`);
                      }}
                    >
                      <Shield className="h-4 w-4 mr-1" />
                      View Risks ({bu.activeRisks ?? bu.totalRisks})
                    </Button>
                  ) : bu.totalRisks > 0 ? (
                    // No assessment, but tagged risks (e.g. Enzoic) are scoped to this business unit —
                    // open the tag-aware All Risks list filtered to DarkWeb + this BU.
                    <Button
                      size="sm"
                      variant="primary"
                      className="px-1 py-1 h-9 text-xs"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/risks?types=DarkWeb&businessUnit=${bu.businessUnitId}`);
                      }}
                      title="View tagged risks for this business unit"
                    >
                      <Shield className="h-4 w-4 mr-1" />
                      View Risks ({bu.activeRisks ?? bu.totalRisks})
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="primary"
                      className="px-1 py-1 h-9 text-xs"
                      disabled
                      title="No risks"
                    >
                      <Shield className="h-4 w-4 mr-1" />
                      View Risks (0)
                    </Button>
                  )}

                  {canManageOrganizationRisks() && bu.latestAssessment?.darkWebAssessmentID && (
                    <Button size="sm" variant="warning" className="px-1 py-1 h-9 text-xs"
                      disabled={bu.latestAssessment?.darkWebStatus !== 'completed'}
                      onClick={(e) => {
                        e.stopPropagation();
                        setAssessmentToReassess(bu.latestAssessment.darkWebAssessmentID);
                        setShowReassessModal(true);
                      }}>
                      <RefreshCw className="h-4 w-4 mr-1" />
                      Reassess
                    </Button>
                  )}

                  {canDeleteAssessment && bu.latestAssessment?.darkWebAssessmentID && (
                    <Button
                      size="sm"
                      variant="danger"
                      className="px-1 py-1 h-9 text-xs"
                      onClick={(e) => {
                        e.stopPropagation();
                        setAssessmentToDelete(bu.latestAssessment.darkWebAssessmentID);
                        setShowDeleteModal(true);
                      }}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete
                    </Button>
                  )}
                    </div>
                  </div>
                  {bu.latestAssessment?.darkWebAssessmentID && (
                    <>
                      <div className="w-px bg-gray-200 self-stretch flex-shrink-0 mx-1"></div>
                      <div className="w-80 flex-shrink-0 bg-gray-50 rounded-xl border border-gray-200 px-4 py-3 shadow-sm">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Activity</p>
                        <AssessmentActivityLog assessmentType="darkweb" assessmentId={String(bu.latestAssessment.darkWebAssessmentID)} hideQuestionEvents />
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Pagination Controls */}
          <Pagination
            page={currentPage}
            totalPages={totalPages}
            totalItems={totalCount}
            pageSize={pageSize}
            onPageChange={setCurrentPageParam}
            onPageSizeChange={setPageSize}
            scrollTargetRef={listTopRef}
            busy={paging}
          />
        </Card>

        <DarkWebNewBusinessUnitModal
          isOpen={showAddBUModal}
          onClose={() => setShowAddBUModal(false)}
          onBusinessUnitAdded={handleBusinessUnitAdded}
          existingBUIds={businessUnits.map((bu) => bu.businessUnitId)}
        />

        {showAssignModal && selectedEntityForAssign && (
          <DealAssignAssessModal
            isOpen={showAssignModal}
            onClose={() => setShowAssignModal(false)}
            dealID={selectedEntityForAssign.businessUnitId}
            businessUnitId={selectedEntityForAssign.businessUnitId}
            assessmentType="darkweb"
            organizationId={JSON.parse(localStorage.getItem("selectedOrganization"))?.organizationId}
            hasExistingAssessment={selectedEntityForAssign.hasAssessment}
            refreshData={fetchBusinessUnits}
          />
        )}

        <AssessmentRespondentsModal
          isOpen={respondentsAssessmentId != null}
          onClose={() => setRespondentsAssessmentId(null)}
          assessmentType="darkweb"
          assessmentId={respondentsAssessmentId}
          refreshData={fetchBusinessUnits}
        />

        <DeleteAssessmentModal
          isOpen={showDeleteModal}
          onClose={() => { setShowDeleteModal(false); setAssessmentToDelete(null); }}
          onConfirm={handleDeleteAssessment}
          isDeleting={deleting}
        />

        <ReassessModal
          isOpen={showReassessModal}
          onClose={() => { setShowReassessModal(false); setAssessmentToReassess(null); }}
          onConfirm={handleReassessAssessment}
          isReassessing={reassessing}
        />

        <AssessmentUnassignedModal
          isOpen={showUnassignedAlert}
          onClose={() => setShowUnassignedAlert(false)}
        />
      </div>
    </div>
  );
};

export default DarkWebBusinessUnits;
